/**
 * Client-side upload rules. Mirrors the backend's shared validator
 * (`apps/common/validators.py`): extension + MIME type must match an allowed
 * kind and the file must be <= 10 MB. The backend re-checks everything
 * (including magic bytes) and answers 400 `{ "error": "..." }` on refusal.
 */

import i18n from "../i18n";

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const MAX_MB = MAX_UPLOAD_BYTES / (1024 * 1024);

type UploadKind = "document" | "image" | "pdf";

const TYPES: Record<string, readonly string[]> = {
  pdf: ["application/pdf", "application/x-pdf"],
  doc: ["application/msword"],
  docx: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  jpg: ["image/jpeg", "image/pjpeg"],
  jpeg: ["image/jpeg", "image/pjpeg"],
  png: ["image/png", "image/x-png"],
};

const EXTENSIONS: Record<UploadKind, readonly string[]> = {
  document: ["pdf", "doc", "docx", "jpg", "jpeg", "png"],
  image: ["jpg", "jpeg", "png"],
  pdf: ["pdf"],
};

const kindLabel = (kind: UploadKind): string => i18n.t(`domain:upload.kinds.${kind}`);

/** Value for the `accept` attribute of a file input of the given kind. */
export const uploadAccept = (kind: UploadKind): string => {
  const exts = EXTENSIONS[kind];
  const mimes = Array.from(new Set(exts.flatMap((e) => TYPES[e].slice(0, 1))));
  return [...exts.map((e) => `.${e}`), ...mimes].join(",");
};

/** Human-readable hint, e.g. "PDF, DOC, DOCX, JPG or PNG (max. 10 MB)". */
export const uploadHint = (kind: UploadKind): string =>
  i18n.t("domain:upload.hint", { types: kindLabel(kind), maxMb: MAX_MB });

/** Returns a user-facing error message, or null when the file is acceptable. */
export const validateUploadFile = (file: File, kind: UploadKind): string | null => {
  if (file.size === 0) return i18n.t("domain:upload.empty");
  if (file.size > MAX_UPLOAD_BYTES) {
    return i18n.t("domain:upload.tooLarge", { name: file.name, maxMb: MAX_MB });
  }
  const ext = file.name.includes(".") ? (file.name.split(".").pop() ?? "").toLowerCase() : "";
  if (!EXTENSIONS[kind].includes(ext)) {
    return i18n.t("domain:upload.unsupportedType", { name: file.name, types: kindLabel(kind) });
  }
  // Browsers occasionally report an empty type; the backend still checks the
  // file signature, so only reject a type that is present and wrong.
  if (file.type && !TYPES[ext].includes(file.type.toLowerCase())) {
    return i18n.t("domain:upload.invalidContent", { name: file.name, ext: ext.toUpperCase(), types: kindLabel(kind) });
  }
  return null;
};
